/**
 * TPL Trust Block — carousel controller.
 * Handles dot navigation, slide tracking, counter updates, and one-time auto-nudge.
 * Scroll-snap does the actual swipe — JS only drives indicators + accessibility.
 */
(function () {
  'use strict';

  const SELECTOR = '[data-tpl-trust-block]';
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(root) {
    const carousel = root.querySelector('[data-tpl-trust-carousel]');
    if (!carousel) return;

    const slides = Array.from(carousel.children);
    if (slides.length <= 1) return;

    const dots = Array.from(root.querySelectorAll('[data-tpl-trust-dot]'));
    const currentEl = root.querySelector('[data-tpl-trust-current]');
    const nudgeDelay = parseInt(root.dataset.autoNudge || '0', 10);

    let activeIdx = 0;
    let userInteracted = false;
    let nudged = false;

    function getSlideWidth() {
      if (slides.length < 2) return slides[0].offsetWidth;
      return slides[1].offsetLeft - slides[0].offsetLeft;
    }

    function updateIndicators(idx) {
      if (idx === activeIdx) return;
      activeIdx = idx;
      dots.forEach((d, i) => {
        const active = i === idx;
        d.classList.toggle('tpl-trust-block__dot--active', active);
        if (active) {
          d.setAttribute('aria-current', 'true');
        } else {
          d.removeAttribute('aria-current');
        }
      });
      if (currentEl) {
        currentEl.textContent = String(idx + 1).padStart(2, '0');
      }
      root.dispatchEvent(new CustomEvent('tpl:trust-block:slide-change', {
        bubbles: true,
        detail: { index: idx, total: slides.length }
      }));
    }

    function onScroll() {
      userInteracted = true;
      const slideWidth = getSlideWidth();
      if (slideWidth <= 0) return;
      const idx = Math.min(slides.length - 1, Math.round(carousel.scrollLeft / slideWidth));
      updateIndicators(idx);
    }

    function goTo(idx) {
      userInteracted = true;
      const slideWidth = getSlideWidth();
      carousel.scrollTo({
        left: slideWidth * idx,
        behavior: REDUCED_MOTION ? 'auto' : 'smooth'
      });
    }

    let rafId = 0;
    carousel.addEventListener('scroll', () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        onScroll();
        rafId = 0;
      });
    }, { passive: true });

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.dataset.tplTrustDot, 10);
        if (!Number.isNaN(idx)) goTo(idx);
      });
    });

    carousel.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goTo(Math.min(slides.length - 1, activeIdx + 1));
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goTo(Math.max(0, activeIdx - 1));
      } else if (e.key === 'Home') {
        e.preventDefault();
        goTo(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        goTo(slides.length - 1);
      }
    });

    ['touchstart', 'pointerdown', 'mousedown'].forEach((evt) => {
      carousel.addEventListener(evt, () => { userInteracted = true; }, { passive: true, once: true });
    });

    if (nudgeDelay > 0 && !REDUCED_MOTION) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          setTimeout(() => {
            if (!userInteracted && !nudged) {
              nudged = true;
              goTo(1);
            }
          }, nudgeDelay * 1000);
        });
      }, { threshold: 0.5 });
      observer.observe(root);
    }
  }

  function initAll() {
    document.querySelectorAll(SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
